// User Guide — /guide
//
// Comprehensive reference for Kanji Build. Practical descriptions sit at
// the top of each section; the deep historical / linguistic context is
// tucked into <Accordion> panels so the page reads well for a novice
// and still rewards a scholar.
//
// Chromeless like Legal.jsx so it's reachable without the entitlement
// gate and without the app chrome (this way it also works as a public
// marketing document).

import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CONTACT_EMAIL = "support@kanjibuild.com";
const EFFECTIVE = "26 February 2026";

/* ── Shell (mirrors Legal.jsx look) ───────────────────────────────── */
function GuideShell({ children }) {
  return (
    <div data-testid="page-guide" className="min-h-screen bg-washi text-sumi">
      <header className="border-b border-borderDefault px-6 md:px-10 py-4 flex items-center justify-between sticky top-0 z-30 bg-washi/90 backdrop-blur-sm">
        <Link to="/" className="font-serif text-2xl text-shu" data-testid="guide-home-link">
          寺
        </Link>
        <div className="flex items-center gap-5">
          <Link
            to="/welcome"
            data-testid="guide-back-welcome"
            className="font-sans text-[11px] uppercase tracking-[0.25em] text-sumiSoft hover:text-sumi transition-colors"
          >
            ← welcome
          </Link>
          <Link
            to="/"
            data-testid="guide-open-app"
            className="font-sans text-[11px] uppercase tracking-[0.25em] text-shu hover:text-sumi transition-colors"
          >
            open the app →
          </Link>
        </div>
      </header>
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14 font-serif text-base leading-relaxed">
        {children}
        <footer className="mt-20 pt-8 border-t border-borderDefault font-sans text-xs text-sumiSoft">
          Something unclear? Email{" "}
          <a className="text-shu hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>
          . · <Link className="text-shu hover:underline" to="/privacy">privacy</Link> ·{" "}
          <Link className="text-shu hover:underline" to="/terms">terms</Link>
        </footer>
      </article>
    </div>
  );
}

/* ── Small building blocks ────────────────────────────────────────── */

function H2({ id, kanji, kicker, children }) {
  return (
    <div className="mt-14 mb-4">
      <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-sumiSoft mb-1">
        {kicker}
      </p>
      <h2
        id={id}
        className="font-serif text-3xl md:text-4xl text-sumi flex items-baseline gap-3 scroll-mt-24"
      >
        {kanji && <span className="text-shu leading-none">{kanji}</span>}
        <span>{children}</span>
      </h2>
    </div>
  );
}

function H3({ children }) {
  return (
    <h3 className="font-serif text-xl md:text-2xl text-sumi mt-8 mb-2">{children}</h3>
  );
}

// Inline "chip" that visually references a UI control from the app.
function UI({ children }) {
  return (
    <span className="inline-block border border-borderDefault px-1.5 py-0.5 font-sans text-[11px] uppercase tracking-[0.15em] text-sumi bg-washi/50 leading-tight align-baseline">
      {children}
    </span>
  );
}

// A small deep-link out to a real in-app surface.
function AppLink({ to, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="text-shu underline underline-offset-4 decoration-shu/40 hover:decoration-shu transition-colors"
    >
      {children}
    </Link>
  );
}

// Kanji rendered inline, in the app's serif type. Optional deep-link.
function K({ ch, to }) {
  const body = <span className="font-serif text-sumi text-lg align-baseline">{ch}</span>;
  if (!to) return body;
  return (
    <Link
      to={to}
      data-testid={`guide-kanji-${ch}`}
      className="text-sumi hover:text-shu transition-colors underline-offset-4 decoration-transparent hover:decoration-shu"
    >
      {body}
    </Link>
  );
}

/* ── Page ─────────────────────────────────────────────────────────── */

export default function Guide() {
  return (
    <GuideShell>
      {/* ── Title ────────────────────────────────────────────────── */}
      <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-sumiSoft mb-2">
        the manual
      </p>
      <h1 className="font-serif text-4xl md:text-6xl text-sumi mb-2 leading-tight">
        Kanji Build <span className="text-shu">user guide</span>
      </h1>
      <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-sumiSoft mb-8">
        version 1 · updated {EFFECTIVE}
      </p>

      <p className="text-sumi/90 mb-4">
        Kanji Build is not a flashcard app. It is a{" "}
        <em>relationship map</em> for the ~2,800 characters used in modern
        written Japanese. Every screen answers one of three questions:
      </p>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-8 text-sumi/90">
        <li>
          <b>What is this kanji made of?</b> — visual and semantic
          decomposition.
        </li>
        <li>
          <b>Where else does this piece appear?</b> — the productive
          radical &amp; phonetic families.
        </li>
        <li>
          <b>What sounds and words fall out of that structure?</b> —
          on&apos;yomi phonetic series and the compound vocabulary that uses
          them.
        </li>
      </ul>
      <p className="text-sumi/90">
        The guide below runs through every feature, links straight into
        the app so you can try each one, then finishes with three worked{" "}
        <a href="#journeys" className="text-shu underline underline-offset-4 hover:decoration-shu">
          learning journeys
        </a>{" "}
        that chain the features together. The historical background —
        oracle-bone script through the modern Jōyō list — sits inside the
        expandable panels so you can dive in when you want to and skip
        past them when you don&apos;t.
      </p>

      {/* ── TOC ─────────────────────────────────────────────────── */}
      <nav
        data-testid="guide-toc"
        aria-label="On this page"
        className="mt-10 mb-4 border border-borderDefault p-5 bg-washi/60"
      >
        <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-sumiSoft mb-3">
          on this page
        </p>
        <ol className="grid md:grid-cols-2 gap-x-6 gap-y-1.5 font-sans text-sm text-sumi list-decimal list-inside">
          <li><a className="hover:text-shu" href="#quickstart">Quick start</a></li>
          <li><a className="hover:text-shu" href="#pillars">The three lenses</a></li>
          <li><a className="hover:text-shu" href="#home">Home &amp; the four browse modes</a></li>
          <li><a className="hover:text-shu" href="#focus">The Kanji focus card</a></li>
          <li><a className="hover:text-shu" href="#vocab">Vocab focus</a></li>
          <li><a className="hover:text-shu" href="#atlas">Phonetic Atlas</a></li>
          <li><a className="hover:text-shu" href="#study">Study</a></li>
          <li><a className="hover:text-shu" href="#journal">Journal</a></li>
          <li><a className="hover:text-shu" href="#kana">Kana etymology chips</a></li>
          <li><a className="hover:text-shu" href="#progress">Progress statuses</a></li>
          <li><a className="hover:text-shu" href="#search">Search</a></li>
          <li><a className="hover:text-shu" href="#demo">The 90-second guided demo</a></li>
          <li><a className="hover:text-shu" href="#account">Sign in, lifetime unlock &amp; install</a></li>
          <li><a className="hover:text-shu" href="#history">Historical background</a></li>
          <li><a className="hover:text-shu" href="#journeys">Deep-learn journeys</a></li>
          <li><a className="hover:text-shu" href="#glossary">Glossary</a></li>
        </ol>
      </nav>

      {/* ── Quick Start ─────────────────────────────────────────── */}
      <H2 id="quickstart" kicker="1" kanji="始">Quick start</H2>
      <ol className="list-decimal list-outside ml-5 space-y-2 mb-4 text-sumi/90">
        <li>
          Play the{" "}
          <AppLink to="/welcome" testid="guide-qs-demo">
            90-second guided tour
          </AppLink>{" "}
          — it drives the real app and shows the three lenses in
          motion.
        </li>
        <li>
          Open the{" "}
          <AppLink to="/" testid="guide-qs-home">
            Home page
          </AppLink>{" "}
          and pick a browse mode: <UI>JLPT</UI>, <UI>Strokes</UI>,{" "}
          <UI>Radicals</UI>, or <UI>Phonetic</UI>.
        </li>
        <li>
          Tap any kanji to open its <em>focus card</em>. Read its
          etymology, then follow either the &ldquo;built from&rdquo; row (its
          components) or the &ldquo;used in&rdquo; row (larger kanji that
          contain it).
        </li>
        <li>
          Mark what you know with the{" "}
          <UI>New</UI> / <UI>Learning</UI> / <UI>Known</UI> /{" "}
          <UI>Hide</UI> buttons — this feeds the &ldquo;ready to learn&rdquo;
          highlight and the compound previews everywhere else.
        </li>
        <li>
          When you spot a family that all shares one reading, jump into
          the{" "}
          <AppLink to="/atlas" testid="guide-qs-atlas">
            Phonetic Atlas
          </AppLink>{" "}
          to see the rest of that sound family.
        </li>
      </ol>

      {/* ── The three lenses ─────────────────────────────────────── */}
      <H2 id="pillars" kicker="2" kanji="観">The three lenses</H2>
      <p className="mb-3">
        Every feature in Kanji Build resolves to one of three ways of
        looking at a character. Knowing which lens you&apos;re using at any
        moment is the single most useful thing to internalise.
      </p>
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="border border-borderDefault p-4">
          <p className="font-serif text-4xl text-sumi leading-none mb-2">意</p>
          <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-sumiSoft mb-2">
            semantic
          </p>
          <p className="font-sans text-sm text-sumi">
            Meaning built from meaning. Radicals combine into ideographs;
            ideographs stack into compounds. The <em>radicals</em> browse
            mode and the <em>built from / used in</em> rows on the focus
            card use this lens.
          </p>
        </div>
        <div className="border border-borderDefault p-4">
          <p className="font-serif text-4xl text-sumi leading-none mb-2">形</p>
          <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-sumiSoft mb-2">
            visual
          </p>
          <p className="font-sans text-sm text-sumi">
            Shape and stroke count. The <em>strokes</em> browse mode, the
            animated stroke SVGs on each focus card, and the KanjiVG
            decomposition tree use this lens.
          </p>
        </div>
        <div className="border border-borderDefault p-4">
          <p className="font-serif text-4xl text-sumi leading-none mb-2">音</p>
          <p className="font-sans text-[10px] uppercase tracking-[0.25em] text-sumiSoft mb-2">
            phonetic
          </p>
          <p className="font-sans text-sm text-sumi">
            One glyph, one Sino-Japanese reading. The <em>phonetic</em>{" "}
            browse mode, the Phonetic Atlas, and the katakana-origin
            chips all live here.
          </p>
        </div>
      </div>

      {/* ── Home & browse modes ─────────────────────────────────── */}
      <H2 id="home" kicker="3" kanji="家">Home &amp; the four browse modes</H2>
      <p className="mb-3">
        The <AppLink to="/" testid="guide-home-link-inline">Home page</AppLink> is
        the main index. A single <UI>browse by</UI> toggle at the top
        switches which lens you&apos;re navigating through:
      </p>
      <ul className="list-disc list-outside ml-5 space-y-3 mb-4 text-sumi/90">
        <li>
          <b>JLPT</b> — N5 → N1. The path is chaptered into stages that
          reveal each level&apos;s new kanji <em>only after</em> its
          prerequisites are marked known. This is the closest thing to a
          traditional textbook progression.
        </li>
        <li>
          <b>Strokes</b> — buckets of 1 → 14, then 15+. Useful for the
          novice tracing period and for hunting a character whose
          reading you&apos;ve forgotten. Secondary sort <UI>freq</UI> vs{" "}
          <UI>alpha</UI> reshuffles inside each bucket.
        </li>
        <li>
          <b>Radicals</b> — 382 productive semantic components, sorted
          by frequency or by the number of Jōyō kanji they generate. Tap
          any radical to see every kanji it appears inside.
        </li>
        <li>
          <b>Phonetic</b> — jumps to the{" "}
          <AppLink to="/atlas" testid="guide-home-atlas">Phonetic Atlas</AppLink>
          {" "}(see below) for browsing by shared on&apos;yomi driver.
        </li>
      </ul>
      <p className="text-sumi/90">
        Below each mode selector sits a progress ribbon: how many
        characters in the current view are marked <em>known</em>,
        {" "}<em>learning</em>, or still <em>new</em>. Kanji tiles are ringed
        in the same colours so the state is obvious at a glance.
      </p>

      {/* ── Kanji focus card ────────────────────────────────────── */}
      <H2 id="focus" kicker="4" kanji="漢">The Kanji focus card</H2>
      <p className="mb-3">
        Every tile leads here. The card is a single scroll and layers
        the three lenses in order:
      </p>

      <H3>Header</H3>
      <p className="mb-3">
        Giant serif glyph, meaning gloss, and its JLPT badge. Below the
        header sits the animated stroke sequence — tap to replay. The
        strokes are drawn in the correct pedagogical order (top-down,
        left-right, enclose-then-fill) so you can trace along.
      </p>

      <H3>On&apos;yomi &amp; kun&apos;yomi</H3>
      <p className="mb-3">
        Two rows of reading chips — <em>on&apos;yomi</em> (Sino-Japanese,
        katakana) and <em>kun&apos;yomi</em> (native Japanese, hiragana).
        Each on&apos;yomi chip is a link that opens the Phonetic Atlas
        pre-filtered to that reading, so you can immediately see who
        else shares this sound.
      </p>

      <H3>Katakana origin chip (if applicable)</H3>
      <p className="mb-3">
        A small chip appears when the character is the historical{" "}
        <em>source</em> of a katakana glyph (e.g. <K ch="加" to="/kanji/%E5%8A%A0" />{" "}
        → カ). See the{" "}
        <a href="#kana" className="text-shu underline underline-offset-4">
          kana etymology
        </a>{" "}
        section below for the full explanation.
      </p>

      <H3>Built from · Used in</H3>
      <p className="mb-3">
        Two horizontal rows of component chips:
      </p>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-3 text-sumi/90">
        <li>
          <b>Built from</b> — every KanjiVG element inside this glyph,
          tappable. The interactive tree beneath renders the same
          decomposition graphically.
        </li>
        <li>
          <b>Used in</b> — every larger kanji that contains this one as
          a component. This is the &ldquo;learn 1, unlock N&rdquo; row.
          A subtle <UI>ready to learn</UI> flag lights up when
          <em> all</em> of that kanji&apos;s components are already marked
          known — a strong signal that the compound will be trivial to
          add next.
        </li>
      </ul>

      <H3>Phonetic driver &amp; siblings</H3>
      <p className="mb-3">
        If this glyph belongs to a <em>keisei-moji</em> (phonetic-semantic
        compound) family, the driver character appears here with its
        siblings. Reading Chinese wasn&apos;t random — around three quarters
        of Jōyō kanji were formed this way, and the driver almost
        always survives as the on&apos;yomi. See{" "}
        <a href="#history-keisei" className="text-shu underline underline-offset-4">
          the historical section
        </a>{" "}
        for the mechanics.
      </p>

      <H3>Compounds</H3>
      <p className="mb-3">
        A live list of real words that use this kanji, colour-coded by
        the status of their constituent kanji: green if you know all
        pieces, amber if some, grey if you&apos;re missing components. This
        is the same &ldquo;ready to learn&rdquo; heuristic applied to
        vocabulary — click any word to open its{" "}
        <a href="#vocab" className="text-shu underline underline-offset-4">
          vocab focus card
        </a>
        .
      </p>

      <H3>Progress &amp; mnemonic</H3>
      <p>
        A shared control at the bottom lets you mark the kanji as{" "}
        <UI>New</UI>, <UI>Learning</UI>, <UI>Known</UI>, or{" "}
        <UI>Hide</UI>. Above it, a plain-text editor stores <em>your</em>
        {" "}mnemonic — a private note that shows up wherever the kanji
        appears in the app. Notes sync to your account when you sign in.
      </p>

      {/* ── Vocab focus ─────────────────────────────────────────── */}
      <H2 id="vocab" kicker="5" kanji="語">Vocab focus</H2>
      <p className="mb-3">
        The same card shape as the kanji focus, but scoped to a
        multi-character compound. It splits the word into its
        constituent kanji chips, shows the reading and translation,
        exposes the same progress controls, and lets you jump into any
        component character with one tap. Reached by tapping any word in
        a compound list or via the search bar.
      </p>

      {/* ── Phonetic Atlas ──────────────────────────────────────── */}
      <H2 id="atlas" kicker="6" kanji="音">Phonetic Atlas</H2>
      <p className="mb-3">
        The <AppLink to="/atlas" testid="guide-atlas-link">Phonetic Atlas</AppLink>{" "}
        is a single-page index of every on&apos;yomi in the dataset, grouped
        by gojūon initial (ア, カ, サ, タ, ナ, ハ, マ, ヤ, ラ, ワ). Under
        each initial:
      </p>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-3 text-sumi/90">
        <li>
          Every reading has a <em>driver</em> — the seed character whose
          shape the whole family inherits. Drivers are drawn in shu red.
        </li>
        <li>
          Under each driver, the <em>family members</em> that share the
          reading are listed. A filter above the atlas lets you show
          only readings with 3+ members, or only readings whose driver
          is itself absent from the Jōyō list (&ldquo;ghost drivers&rdquo;).
        </li>
        <li>
          The <UI>reverse lookup</UI> panel accepts a katakana glyph and
          returns every phonetic driver that produces that sound —
          useful for the &ldquo;I heard <em>shou</em> but which kanji?&rdquo; case.
        </li>
      </ul>
      <p className="text-sumi/90">
        The atlas is the fastest way to understand why beginners
        legitimately confuse <K ch="待" to="/kanji/%E5%BE%85" />,{" "}
        <K ch="持" to="/kanji/%E6%8C%81" />,{" "}
        <K ch="時" to="/kanji/%E6%99%82" /> and{" "}
        <K ch="詩" to="/kanji/%E8%A9%A9" /> — they all inherit タイ / ジ /
        シ from the same driver <K ch="寺" to="/kanji/%E5%AF%BA" />.
      </p>

      {/* ── Study ───────────────────────────────────────────────── */}
      <H2 id="study" kicker="7" kanji="学">Study</H2>
      <p>
        The <AppLink to="/study" testid="guide-study-link">Study page</AppLink> is
        a spaced-list of the characters you have currently marked as
        <em> learning</em>, ordered so that the ones whose components you
        already know surface first. Tap any tile to jump into its focus
        card, then use the progress buttons to promote it to <UI>Known</UI>
        {" "}or send it back to <UI>New</UI>.
      </p>

      {/* ── Journal ─────────────────────────────────────────────── */}
      <H2 id="journal" kicker="8" kanji="録">Journal</H2>
      <p>
        The <AppLink to="/journal" testid="guide-journal-link">Journal</AppLink>{" "}
        is a chronological feed of every mnemonic you&apos;ve written and
        every character you&apos;ve promoted to <em>known</em>. Think of it
        as a lab notebook — a record of your thinking, not a review
        deck. Each entry links back to its focus card.
      </p>

      {/* ── Kana etymology ──────────────────────────────────────── */}
      <H2 id="kana" kicker="9" kanji="仮">Kana etymology chips</H2>
      <p className="mb-3">
        Every katakana glyph was originally a shorthand corner of a
        specific kanji. Kanji Build surfaces this in two places:
      </p>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-3 text-sumi/90">
        <li>
          <b>On kanji cards</b> — a chip on the source glyph says
          &ldquo;this is where the kana <em>X</em> came from&rdquo;. Example:
          {" "}<K ch="加" to="/kanji/%E5%8A%A0" /> → カ. If the kanji still
          reads with that sound today (a &ldquo;retained sound&rdquo;), the
          chip flags it and offers to jump you into the phonetic family.
        </li>
        <li>
          <b>Reverse lookup in the Atlas</b> — type a kana into the
          {" "}<AppLink to="/atlas" testid="guide-kana-atlas">Phonetic Atlas</AppLink>{" "}
          reverse panel and see every kanji whose historical
          simplification produced that shape. Roughly half of the modern
          katakana set has a productive kanji source, so this is a real
          navigation tool, not a curiosity.
        </li>
      </ul>

      {/* ── Progress statuses ───────────────────────────────────── */}
      <H2 id="progress" kicker="10" kanji="印">Progress statuses</H2>
      <p className="mb-3">
        Four states, applied per character and per compound. They drive
        the &ldquo;ready to learn&rdquo; highlight, the compound colour-coding,
        and the visible progress ribbons on Home.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="border border-borderDefault p-3">
          <p className="font-serif text-lg text-sumi mb-1">New</p>
          <p className="font-sans text-xs text-sumiSoft">
            Default. Not yet seen or engaged with.
          </p>
        </div>
        <div className="border-2 p-3" style={{ borderColor: "#D29C4C" }}>
          <p className="font-serif text-lg mb-1" style={{ color: "#D29C4C" }}>
            Learning
          </p>
          <p className="font-sans text-xs text-sumiSoft">
            Actively being worked on. Surfaces in Study.
          </p>
        </div>
        <div className="border-2 p-3" style={{ borderColor: "#6E7A5A" }}>
          <p className="font-serif text-lg mb-1" style={{ color: "#6E7A5A" }}>
            Known
          </p>
          <p className="font-sans text-xs text-sumiSoft">
            Counts as a prerequisite for &ldquo;ready to learn&rdquo;.
          </p>
        </div>
        <div className="border-2 p-3" style={{ borderColor: "#B45A4C" }}>
          <p className="font-serif text-lg mb-1" style={{ color: "#B45A4C" }}>
            Hide
          </p>
          <p className="font-sans text-xs text-sumiSoft">
            Suppresses the character everywhere. Use for kanji you
            genuinely don&apos;t need (rare Jinmeiyō names, etc.).
          </p>
        </div>
      </div>
      <p className="text-sumi/90">
        Progress lives locally by default and syncs to your account once
        you&apos;ve signed in — see{" "}
        <a href="#account" className="text-shu underline underline-offset-4">
          the account section
        </a>
        .
      </p>

      {/* ── Search ──────────────────────────────────────────────── */}
      <H2 id="search" kicker="11" kanji="探">Search</H2>
      <p>
        The search bar in the header accepts three inputs: a single
        kanji, a hiragana / katakana / rōmaji reading, or a whole
        compound word. Results are sectioned by kind (single characters
        first, then multi-character words) and each result opens its
        focus card. There is no fuzzy matching by design — searches are
        deterministic so &ldquo;I meant that one&rdquo; never happens.
      </p>

      {/* ── Demo ────────────────────────────────────────────────── */}
      <H2 id="demo" kicker="12" kanji="旅">The 90-second guided demo</H2>
      <p>
        The <UI>90-sec demo</UI> button in the top header (and the
        &ldquo;take the tour&rdquo; buttons on{" "}
        <AppLink to="/welcome" testid="guide-demo-welcome">the Welcome page</AppLink>)
        drives the real app through a scripted seven-beat tour. It is
        the fastest way to see all three lenses in motion. The demo can
        be resumed from any beat by starting it again — it fast-forwards
        past anything you&apos;ve already completed.
      </p>

      {/* ── Account / unlock / install ──────────────────────────── */}
      <H2 id="account" kicker="13" kanji="鍵">Sign in, lifetime unlock &amp; install</H2>
      <ul className="list-disc list-outside ml-5 space-y-3 mb-4 text-sumi/90">
        <li>
          <b>Sign in</b> — via Google. We ask only for your email, name
          and avatar (see the{" "}
          <Link to="/privacy" className="text-shu underline underline-offset-4">
            privacy policy
          </Link>
          ). Signing in enables cross-device progress sync.
        </li>
        <li>
          <b>Lifetime unlock</b> — a single £20 payment via Stripe
          removes the demo gate. The refund policy is set out in the{" "}
          <Link to="/terms" className="text-shu underline underline-offset-4">
            terms
          </Link>
          .
        </li>
        <li>
          <b>Install to home screen</b> — the <UI>Install app</UI>{" "}
          button on the Welcome page turns Kanji Build into an offline
          Progressive Web App on Android, iOS, Chrome and Edge.
        </li>
      </ul>

      {/* ── History ─────────────────────────────────────────────── */}
      <H2 id="history" kicker="14" kanji="史">Historical background</H2>
      <p className="mb-4">
        Kanji Build&apos;s features only make sense against the actual
        history of the script. These panels are optional — expand
        whichever ones catch your eye.
      </p>

      <Accordion type="multiple" className="mb-4" data-testid="guide-history-accordion">
        {/* Panel: script evolution */}
        <AccordionItem value="script">
          <AccordionTrigger data-testid="history-script">
            <span className="font-serif text-lg text-sumi">
              From oracle bones to modern kaisho — why kanji look the way they do
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 font-serif text-base text-sumi/90 leading-relaxed pl-1">
              <p>
                Chinese characters have a documented 3,300-year lineage.
                The earliest surviving forms — carved into ox scapulae
                and turtle plastrons for divination in the late Shang
                dynasty (c. 1250 BCE) — are called <em>jiǎgǔwén</em>{" "}
                (甲骨文, &ldquo;shell-and-bone writing&rdquo;). They are
                pictographic and idiosyncratic: dozens of variants for
                the same word, angular strokes suited to a knife on bone.
              </p>
              <p>
                Standardisation began under Qín Shǐ Huáng (221 BCE), whose
                chancellor Lǐ Sī unified the regional scripts into{" "}
                <em>xiǎozhuàn</em> (小篆, small seal). Han-dynasty scribes
                then flattened seal script&apos;s curves into brush-friendly
                <em> lìshū</em> (隷書, clerical script), whose horizontals
                and dropped verticals still visibly shape modern kanji.
              </p>
              <p>
                Regular script — <em>kǎishū</em> in Chinese,{" "}
                <em>kaisho</em> 楷書 in Japanese — solidified around the
                3rd–4th century CE and is the direct ancestor of the
                shapes you see in the app&apos;s stroke animations. The
                stroke order Kanji Build teaches (top-to-bottom,
                left-to-right, enclose-then-fill, the single vertical
                after its flanks) was codified during this transition.
              </p>
              <p>
                Japan received a fully-formed kaisho script from China
                via Korea, probably in the 5th century CE. There was no
                indigenous writing to displace; Japanese scribes simply
                adopted the glyphs whole. What happened <em>next</em> —
                bending a foreign meaning-script to fit a completely
                unrelated language — is the story of on&apos;yomi,
                kun&apos;yomi and kana.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Panel: on'yomi layers */}
        <AccordionItem value="onyomi">
          <AccordionTrigger data-testid="history-onyomi">
            <span className="font-serif text-lg text-sumi">
              On&apos;yomi in layers — Go-on, Kan-on, Tō-on
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 font-serif text-base text-sumi/90 leading-relaxed pl-1">
              <p>
                When Japanese speakers borrowed a Chinese character they
                borrowed its Chinese pronunciation too, filtered through
                Japanese phonology. But Chinese was borrowed from repeatedly
                over half a millennium, and each wave preserved a slightly
                different variety of Middle Chinese. The result is that
                many kanji carry two or three on&apos;yomi in stratified
                layers.
              </p>
              <ul className="list-disc list-outside ml-5 space-y-2">
                <li>
                  <b>Go-on 呉音 (5th–6th c.)</b> — the earliest layer,
                  associated with Buddhist vocabulary transmitted via
                  the Wu region of southern China. Example: 明 = <em>myō</em>{" "}
                  (as in 光明 <em>kōmyō</em>, &ldquo;radiance&rdquo;, a Buddhist word).
                </li>
                <li>
                  <b>Kan-on 漢音 (7th–9th c.)</b> — deliberately re-imported
                  during the Tang dynasty by envoys to Chang&apos;an, and
                  eventually declared the &ldquo;correct&rdquo; reading by imperial
                  edict. Example: 明 = <em>mei</em> (as in 明治 <em>meiji</em>).
                </li>
                <li>
                  <b>Tō-on 唐音 / Sō-on 宋音 (13th c. onwards)</b> — a
                  much thinner layer brought back by Zen monks from
                  Southern Song China. Example: 明 = <em>min</em> (as in
                  明朝 <em>minchō</em>, the typeface family).
                </li>
              </ul>
              <p>
                The Phonetic Atlas will happily show you all three
                readings for a single character. Understanding that they
                come from three different centuries is what makes their
                coexistence stop feeling arbitrary.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Panel: kun'yomi */}
        <AccordionItem value="kunyomi">
          <AccordionTrigger data-testid="history-kunyomi">
            <span className="font-serif text-lg text-sumi">
              Kun&apos;yomi — pouring native Japanese into borrowed vessels
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 font-serif text-base text-sumi/90 leading-relaxed pl-1">
              <p>
                Before kanji arrived, Japan was already speaking Old
                Japanese — a language completely unrelated to Chinese. A
                mountain was already <em>yama</em>; there was no need for
                a new word, only for a way to write it down. The
                pragmatic solution was to reuse the Chinese character
                for &ldquo;mountain&rdquo; (山) with its native pronunciation
                attached. That native reading is the <em>kun&apos;yomi</em>{" "}
                (訓読み, &ldquo;explanatory reading&rdquo;).
              </p>
              <p>
                Most Jōyō kanji therefore carry <em>both</em> readings:
                one or more on&apos;yomi imported from China, plus one or
                more kun&apos;yomi from the native Yamato vocabulary. Which
                one applies depends on the context — as a very rough
                rule, single kanji standing alone in words tend to take
                the kun reading; compounds of two or more kanji tend to
                take on readings.
              </p>
              <p>
                The two rows of reading chips on every focus card make
                this split explicit. Katakana for the imported readings,
                hiragana for the native ones — the same convention Japanese
                dictionaries have used for the last century.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Panel: kana */}
        <AccordionItem value="kana">
          <AccordionTrigger data-testid="history-kana">
            <span className="font-serif text-lg text-sumi">
              Man&apos;yōgana → hiragana &amp; katakana
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 font-serif text-base text-sumi/90 leading-relaxed pl-1">
              <p>
                A meaning-script cannot easily record grammatical
                particles or verb inflections. The Nara-period fix,
                systematised in the 8th-century Man&apos;yōshū poetry
                anthology, was to use kanji purely for their <em>sound</em>{" "}
                — ignoring meaning entirely. This convention is called{" "}
                <em>man&apos;yōgana</em> (万葉仮名). Writing the syllable{" "}
                <em>ka</em> could mean picking 加, 可, 香 or any of a
                dozen other characters that happened to be read that way.
              </p>
              <p>
                The system worked but was ugly and expensive to write.
                Two simplification pressures pulled it in different
                directions:
              </p>
              <ul className="list-disc list-outside ml-5 space-y-2">
                <li>
                  <b>Hiragana</b> — a cursive simplification of the
                  <em> whole</em> character, developed primarily by
                  literate women at the Heian court for private writing
                  and vernacular literature (Sei Shōnagon, Murasaki
                  Shikibu). Rounded, connective, elegant. Example: 安 →
                  安 → <em>あ</em>.
                </li>
                <li>
                  <b>Katakana</b> — a shorthand simplification using a
                  <em> single fragment</em> of the source character,
                  developed at roughly the same time by Buddhist monks
                  taking margin notes on Chinese sūtras. Angular, blocky,
                  designed to be jotted quickly. Example: 加 → the
                  left-hand element becomes <em>カ</em>.
                </li>
              </ul>
              <p>
                By the 12th century both scripts had stabilised on a
                single source character per syllable. The system was
                still fluid enough that individual scribes had
                preferences until it was formally standardised in the
                Meiji-era language reforms of 1900.
              </p>
              <p>
                Kanji Build&apos;s <em>katakana origin chips</em> restore
                the productive half of this history — telling you which
                specific kanji is the source of each kana. For roughly
                half the katakana set the source glyph also still
                <em> reads</em> as that sound today, which is why the
                chip doubles as a phonetic-family entry point.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Panel: keisei-moji */}
        <AccordionItem value="keisei" id="history-keisei">
          <AccordionTrigger data-testid="history-keisei">
            <span className="font-serif text-lg text-sumi">
              Keisei-moji — the phonetic-semantic engine
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 font-serif text-base text-sumi/90 leading-relaxed pl-1">
              <p>
                The Han-era etymologist Xǔ Shèn organised the entire
                character set into six categories (<em>liùshū</em> 六書,
                &ldquo;the six writings&rdquo;) around 100 CE:
                pictographs, simple ideographs, compound ideographs,
                phonetic-semantic compounds, derivative characters, and
                phonetic loans. The single most productive category by
                far is the fourth — <em>xíngshēng</em> in Chinese,{" "}
                <em>keisei-moji</em> (形声文字) in Japanese, the
                phonetic-semantic compound.
              </p>
              <p>
                The pattern is simple: one component contributes meaning
                (the semantic radical), a second component contributes
                <em> sound</em> (the phonetic driver). Take 寺 (temple),
                originally pronounced something like *dziH in Old
                Chinese, giving Japanese ジ. Add it to:
              </p>
              <ul className="list-disc list-outside ml-5 space-y-1">
                <li>日 &ldquo;sun&rdquo; → 時 &ldquo;time&rdquo;, on&apos;yomi ジ</li>
                <li>手 &ldquo;hand&rdquo; → 持 &ldquo;to hold&rdquo;, on&apos;yomi ジ</li>
                <li>言 &ldquo;speech&rdquo; → 詩 &ldquo;poetry&rdquo;, on&apos;yomi シ</li>
                <li>人 &ldquo;person&rdquo; → 侍 &ldquo;warrior/attend&rdquo;, on&apos;yomi ジ</li>
                <li>彳 &ldquo;go&rdquo; → 待 &ldquo;wait&rdquo;, on&apos;yomi タイ</li>
              </ul>
              <p>
                Estimates for how many Jōyō kanji fit this pattern run
                between 60% and 85% depending on how strictly you count.
                Kanji Build&apos;s Phonetic Atlas is built entirely on this
                observation: learn the driver, and every child character
                becomes a variation on a theme you already know.
              </p>
              <p>
                A note on drift: the phonetic-semantic pattern was
                established in Chinese, not Japanese. Middle Chinese
                distinctions collapsed differently when borrowed at
                different times, so a driver&apos;s Japanese descendants
                sometimes split across two or three modern on&apos;yomi
                (the 寺 family above is a classic case). The atlas keeps
                the families together anyway — the shared driver is the
                point, even if the modern pronunciation has fractured.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Panel: radicals */}
        <AccordionItem value="radicals">
          <AccordionTrigger data-testid="history-radicals">
            <span className="font-serif text-lg text-sumi">
              Radicals — the Kangxi 214 and their productive descendants
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 font-serif text-base text-sumi/90 leading-relaxed pl-1">
              <p>
                A <em>radical</em> (部首 <em>bushu</em>) is a recurring
                sub-component that classical Chinese dictionaries use as
                an indexing key. The canonical set of 214 was fixed by
                the imperial <em>Kāngxī zìdiǎn</em> (康熙字典) in 1716
                and is still the reference used by East Asian dictionaries
                today.
              </p>
              <p>
                Radicals do two jobs at once. Historically they were the
                lookup key — you found a character by counting its
                strokes and then finding it under its assigned radical.
                Semantically, in a keisei-moji (see above), the radical
                is the meaning-bearing half: 水 &ldquo;water&rdquo; when
                stuck to a phonetic gives you a water-related noun (河
                river, 海 sea, 波 wave); 心 &ldquo;heart&rdquo; gives you
                an emotion (思 to think, 意 intent, 情 feeling).
              </p>
              <p>
                Kanji Build&apos;s Radicals browse mode doesn&apos;t restrict
                itself to the Kangxi 214. It surfaces 382 <em>productive</em>{" "}
                components — every element that actually shows up in
                more than one Jōyō or Jinmeiyō kanji. This includes
                non-canonical elements that traditional dictionaries
                ignore but that carry huge learning leverage (e.g. 也 as
                a phonetic base in 地, 池, 他, 施).
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Panel: Meiji reforms & the lists */}
        <AccordionItem value="lists">
          <AccordionTrigger data-testid="history-lists">
            <span className="font-serif text-lg text-sumi">
              Meiji reforms &amp; the modern kanji lists (Tōyō, Jōyō, Jinmeiyō)
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-3 font-serif text-base text-sumi/90 leading-relaxed pl-1">
              <p>
                The Meiji Restoration (1868) turned Japan into a
                nation-state with universal literacy on its target list.
                The pre-modern kanji set was staggering — the 1716
                Kāngxī dictionary lists ~47,000 characters — and clearly
                incompatible with mass schooling and cheap printing. A
                century of iterative reforms followed.
              </p>
              <ul className="list-disc list-outside ml-5 space-y-2">
                <li>
                  <b>1900</b> — hiragana and katakana each collapse to a
                  single canonical glyph per syllable. The old variant
                  forms (<em>hentaigana</em>) drop out of school and
                  government use.
                </li>
                <li>
                  <b>1946</b> — the post-war <em>Tōyō kanji</em> (当用漢字)
                  list defines 1,850 characters for daily use, with
                  simplified <em>shinjitai</em> forms replacing many
                  traditional shapes (學 → 学, 國 → 国, 藝 → 芸).
                </li>
                <li>
                  <b>1981</b> — the <em>Jōyō kanji</em> (常用漢字)
                  list replaces Tōyō with 1,945 characters. Revised
                  November 2010 to add 196 and remove 5, landing on the
                  current <b>2,136</b> Jōyō.
                </li>
                <li>
                  <b>Jinmeiyō 人名用漢字</b> — a supplementary list of
                  characters permitted in personal names but outside the
                  daily-use set. As of 2024 there are 863 Jinmeiyō
                  characters. Kanji Build&apos;s Tier 2 dataset includes
                  the full Jinmeiyō set, bringing the app to{" "}
                  <b>2,828 characters total</b>.
                </li>
              </ul>
              <p>
                In the browse modes you&apos;ll see all of these
                distinctions surface as JLPT badges (N5–N1, which map
                roughly onto Jōyō frequency bands) plus a &ldquo;Jinmeiyō&rdquo;
                flag for the Tier 2 set. If you&apos;re studying for the
                JLPT, stay inside N5 → N1 and ignore the rest. If you&apos;re
                reading historical or literary text, the Jinmeiyō layer
                is where the surprises hide.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* ── Journeys ────────────────────────────────────────────── */}
      <H2 id="journeys" kicker="15" kanji="道">Deep-learn user journeys</H2>
      <p className="mb-6">
        Three worked walk-throughs that chain the features into a
        pedagogical arc. Each takes ten to twenty minutes; each teaches
        you a family of characters, not just individual ones.
      </p>

      {/* Journey A */}
      <div className="border border-borderDefault p-5 md:p-7 mb-6" data-testid="journey-a">
        <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-shu mb-1">
          journey a · phonetic family
        </p>
        <h3 className="font-serif text-2xl md:text-3xl text-sumi mb-4">
          Learn one, unlock seven — the <span className="text-shu">寺</span> family
        </h3>
        <ol className="list-decimal list-outside ml-5 space-y-3 text-sumi/90">
          <li>
            Open{" "}
            <AppLink to="/kanji/%E5%AF%BA" testid="journey-a-step-1">
              the focus card for 寺 (temple, ji)
            </AppLink>
            . Play the stroke animation once. Read the &ldquo;built from&rdquo;
            row — 寺 is 土 &ldquo;earth&rdquo; over 寸 &ldquo;a measured
            hand&rdquo;: a piece of land marked out by ritual measure,
            hence a temple compound.
          </li>
          <li>
            Scroll to the <em>used in</em> row. You&apos;ll see seven
            children: 時, 持, 詩, 侍, 待, 特, 等. Note that six of them
            take an on&apos;yomi built around ジ / タイ / シ / トク. The
            Sino-Japanese sound survives across the whole family — this
            is a textbook keisei-moji cluster.
          </li>
          <li>
            Tap the <UI>Known</UI> button on 寺 (assume you know it
            after step 1). Now open{" "}
            <AppLink to="/kanji/%E6%99%82" testid="journey-a-step-3">時</AppLink>{" "}
            (time). The card will flag it as <em>ready to learn</em> —
            all its components (日, 寺) are marked known.
          </li>
          <li>
            From 時&apos;s focus card, tap the on&apos;yomi chip{" "}
            <UI>ジ</UI>. This jumps into the Phonetic Atlas pre-filtered
            to that reading. You&apos;ll see 寺, 時, 持, 侍, 事 and a handful
            of unrelated homophones. The <em>related</em> ones — the
            ones that visually contain 寺 — cluster together under the
            same driver.
          </li>
          <li>
            Repeat the walk for 持 (to hold), 詩 (poetry), and 待 (to
            wait). Each opens under three seconds because you&apos;ve
            already internalised the driver.
          </li>
          <li>
            Finally, back on{" "}
            <AppLink to="/kanji/%E5%AF%BA" testid="journey-a-step-6">寺</AppLink>
            &apos;s card, scroll to <em>compounds</em>. Real words like 寺院
            (temple), 時間 (time), 期待 (expectation), 詩集 (poetry
            collection) all appear — several of them will be highlighted
            green because you now know the underlying kanji.
          </li>
        </ol>
        <p className="mt-4 font-sans text-xs text-sumiSoft leading-relaxed">
          <b>What just happened:</b> instead of six flashcard sessions
          you made <em>one</em> pattern investment (寺, its meaning, its
          driver reading) and cashed it in six times. This is the whole
          product thesis in miniature.
        </p>
      </div>

      {/* Journey B */}
      <div className="border border-borderDefault p-5 md:p-7 mb-6" data-testid="journey-b">
        <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-shu mb-1">
          journey b · radical to reading
        </p>
        <h3 className="font-serif text-2xl md:text-3xl text-sumi mb-4">
          From radical to reading — the <span className="text-shu">木</span> tree
        </h3>
        <ol className="list-decimal list-outside ml-5 space-y-3 text-sumi/90">
          <li>
            Go{" "}
            <AppLink to="/" testid="journey-b-step-1">Home</AppLink> and
            switch <UI>browse by</UI> to <UI>Radicals</UI>. Sort by
            &ldquo;productive&rdquo;. The top row will contain 木, 水, 人, 心
            and 口 — the workhorses.
          </li>
          <li>
            Tap the 木 radical. You&apos;ll get a grid of every kanji that
            uses 木 as a component. Focus on the double and triple
            forms first: 林 (grove, two trees), 森 (forest, three trees).
            Both are <em>compound ideographs</em> — pure meaning
            stacking, no phonetic games.
          </li>
          <li>
            Now tap 校 (school). Read its structure on the focus card:
            木 (wood) + 交 (cross / exchange). The <em>meaning</em>{" "}
            comes from 木 (a school was originally a wooden frame
            structure for practising archery). The <em>reading</em> —{" "}
            コウ — comes from 交. This is a keisei-moji: not two ideas
            fused, but one meaning + one sound.
          </li>
          <li>
            Follow the phonetic sibling row on 校 to{" "}
            <AppLink to="/kanji/%E4%BA%A4" testid="journey-b-step-4">交</AppLink>.
            You&apos;ve now met the driver. In the Phonetic Atlas its family
            includes 郊 (suburb, on&apos;yomi コウ), 較 (comparison, コウ),
            較 &amp; 効 (effect, コウ) and 絞 (to twist / strangle, コウ).
            All コウ. All built on 交.
          </li>
          <li>
            Return to the 木 radical browse. Mark everything you&apos;ve met
            so far as <UI>Known</UI> or <UI>Learning</UI>. Notice how
            the progress ribbon at the top of the browse now shows real
            colour.
          </li>
          <li>
            Open{" "}
            <AppLink to="/study" testid="journey-b-step-6">Study</AppLink>.
            The characters you&apos;ve set to <em>Learning</em> are queued in
            an order that favours ones whose components you already
            know — the drill you&apos;ve just set up feeds itself.
          </li>
        </ol>
        <p className="mt-4 font-sans text-xs text-sumiSoft leading-relaxed">
          <b>What just happened:</b> you used the <em>semantic</em> lens
          (radical browse) to find a related pool, then flipped into the{" "}
          <em>phonetic</em> lens (Atlas) to harvest the sound family
          behind one of its members. Two lenses, one investigation.
        </p>
      </div>

      {/* Journey C */}
      <div className="border border-borderDefault p-5 md:p-7 mb-4" data-testid="journey-c">
        <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-shu mb-1">
          journey c · katakana trail
        </p>
        <h3 className="font-serif text-2xl md:text-3xl text-sumi mb-4">
          Kana as a shortcut — following the <span className="text-shu">カ</span> trail
        </h3>
        <ol className="list-decimal list-outside ml-5 space-y-3 text-sumi/90">
          <li>
            Open{" "}
            <AppLink to="/kanji/%E5%8A%A0" testid="journey-c-step-1">
              the focus card for 加 (to add, ka)
            </AppLink>
            . Notice the small <UI>katakana origin</UI> chip at the top:
            &ldquo;this is where カ came from.&rdquo;
          </li>
          <li>
            The chip flags that カ&apos;s sound is <em>retained</em> — the
            source kanji still reads that way. Tap through to the
            Phonetic Atlas カ section.
          </li>
          <li>
            Under the カ initial you&apos;ll find several drivers: 加, 化,
            可, 家, 果. Each seeds its own family. Tap 化 → see 花
            (flower), 貨 (goods), 靴 (shoes). Same driver-shape, all カ.
          </li>
          <li>
            Now try the <em>reverse</em> lookup. In the Atlas panel,
            type <em>カ</em>. You&apos;ll get the driver 加 back (plus 何
            for カ / なに if included). This is the &ldquo;I saw the kana,
            what kanji made it?&rdquo; workflow.
          </li>
          <li>
            Cross back to hiragana. Open the focus card for a kanji
            whose <em>kun&apos;yomi</em> starts with カ — e.g.{" "}
            <AppLink to="/kanji/%E6%B0%97" testid="journey-c-step-5">気 (spirit)</AppLink>{" "}
            reads き / け, so try <em>か</em>ぜ for 風. Notice that the
            hiragana か is a cursive simplification of the whole kanji{" "}
            <em>安</em>, not a corner of it — a different route entirely
            through the man&apos;yōgana system.
          </li>
          <li>
            Add a personal mnemonic to 加 while you&apos;re there. Type
            something like &ldquo;force (力) added by a voice (口) — the
            kana カ is literally the left half of this kanji.&rdquo; The
            note will follow the character everywhere it appears in the
            app.
          </li>
        </ol>
        <p className="mt-4 font-sans text-xs text-sumiSoft leading-relaxed">
          <b>What just happened:</b> you used a <em>kana</em> as an index
          into the phonetic system, discovered the historical link back
          to the source kanji, and left your own investigation notes for
          future you. The kana chip is not decorative — it&apos;s a
          navigational shortcut.
        </p>
      </div>

      {/* ── Glossary ────────────────────────────────────────────── */}
      <H2 id="glossary" kicker="16" kanji="辞">Glossary</H2>
      <dl className="mb-8 font-serif text-base text-sumi/90">
        <div className="border-b border-borderDefault py-3">
          <dt className="font-semibold text-sumi">Bushu (部首)</dt>
          <dd className="mt-1">
            Radical. A recurring sub-component used as a dictionary index
            key.
          </dd>
        </div>
        <div className="border-b border-borderDefault py-3">
          <dt className="font-semibold text-sumi">Jōyō / Jinmeiyō</dt>
          <dd className="mt-1">
            The official Japanese lists of &ldquo;daily use&rdquo; kanji
            (2,136) and &ldquo;permitted for personal names&rdquo; kanji
            (863). Kanji Build carries both, for 2,828 total.
          </dd>
        </div>
        <div className="border-b border-borderDefault py-3">
          <dt className="font-semibold text-sumi">Keisei-moji (形声文字)</dt>
          <dd className="mt-1">
            A phonetic-semantic compound. One component gives the
            meaning, another gives the sound. Around three quarters of
            Jōyō kanji are formed this way.
          </dd>
        </div>
        <div className="border-b border-borderDefault py-3">
          <dt className="font-semibold text-sumi">On&apos;yomi (音読み)</dt>
          <dd className="mt-1">
            The Sino-Japanese &ldquo;sound reading&rdquo; imported from
            Chinese. Traditionally written in katakana.
          </dd>
        </div>
        <div className="border-b border-borderDefault py-3">
          <dt className="font-semibold text-sumi">Kun&apos;yomi (訓読み)</dt>
          <dd className="mt-1">
            The native Japanese &ldquo;explanatory reading&rdquo; grafted
            onto the imported glyph. Traditionally written in hiragana.
          </dd>
        </div>
        <div className="border-b border-borderDefault py-3">
          <dt className="font-semibold text-sumi">Man&apos;yōgana (万葉仮名)</dt>
          <dd className="mt-1">
            The 8th-century convention of using kanji purely for their
            sound — the direct ancestor of both hiragana and katakana.
          </dd>
        </div>
        <div className="border-b border-borderDefault py-3">
          <dt className="font-semibold text-sumi">Phonetic driver</dt>
          <dd className="mt-1">
            The seed character whose sound is inherited by an entire
            keisei-moji family. Rendered in shu red in the Phonetic
            Atlas.
          </dd>
        </div>
        <div className="border-b border-borderDefault py-3">
          <dt className="font-semibold text-sumi">Ready to learn</dt>
          <dd className="mt-1">
            A visual flag applied to a kanji whose components are all
            already marked known — a signal that the character is one
            small step away.
          </dd>
        </div>
      </dl>

      {/* ── CTA ─────────────────────────────────────────────────── */}
      <div className="mt-12 border border-borderDefault p-6 md:p-8 bg-washi/70 text-center">
        <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-sumiSoft mb-3">
          done reading?
        </p>
        <h3 className="font-serif text-2xl md:text-3xl text-sumi mb-4">
          Try one of the journeys.
        </h3>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link
            to="/kanji/%E5%AF%BA"
            data-testid="guide-cta-journey-a"
            className="inline-block bg-shu text-washi px-5 py-2.5 font-sans text-xs uppercase tracking-[0.25em] hover:bg-sumi transition-colors"
          >
            start with 寺 →
          </Link>
          <Link
            to="/"
            data-testid="guide-cta-home"
            className="inline-block px-5 py-2.5 font-sans text-xs uppercase tracking-[0.25em] text-sumi border border-borderDefault hover:border-shu hover:text-shu transition-colors"
          >
            just open the app
          </Link>
        </div>
      </div>
    </GuideShell>
  );
}
